import { gql, useMutation } from '@apollo/client';
import type { ApolloCache, ApolloError, FetchResult } from '@apollo/client';
import { Obj } from '@livecontrol/core-utils';
import type { Asset } from '@livecontrol/scheduler/model';
import { Event } from '@livecontrol/scheduler/model';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Errors } from '../graphql';
import type { MutationResult } from '../graphql';
import * as Dbx from './dbx';
import { QUERY as query } from './use-events';

interface TVariables {
  input: {
    event: number;
    emitWebsocket?: boolean;
    location: string;
    assetIDs: Asset.ID[];
  };
}

interface TData {
  manualEventStart?: boolean;
}

const MUTATION = gql`
  mutation ManualEventStart($input: EventToActionInput!) {
    manualEventStart(eventToStart: $input)
  }
`;

interface Args {
  event: Event;
}

export const useStart = (clientId: number): [
  (args: Args) => Promise<boolean>,
  MutationResult<boolean, 'success'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);
  const [error, setError] = useState<Error | undefined>();
  const [success, setSuccess] = useState<boolean>();

  return [
    useCallback(
      async ({ event }: Args): Promise<boolean> => {
        let success_ = false;

        try {
          // Parse the input arguments
          const input = {
            event: event.id,
            location: event.location.id,
            // emitWebsocket: true,
            assetIDs: event.assets?.map((asset) => asset.id) ?? []
          };

          if (!Obj.isHydrated(input)) {
            throw Errors.badRequest();
          }

          const response = await mutation({
            variables: { input },
            update(cache: ApolloCache<unknown>) {
              const varUpcoming = {
                event: {
                  extra_filters: {
                    page: 1,
                    limit: 20,
                    client_id: clientId,
                    sort_field: 'start_time',
                    sort_order: 'asc',
                    state: 'upcoming'
                  }
                }
              };

              const liveVariables = {
                event: {
                  extra_filters: {
                    page: 1,
                    limit: 20,
                    client_id: clientId,
                    state: 'live'
                  }
                }
              };

              const upcomingData = cache.readQuery<{ getEventsPaginated: { result: { id: string }[] } }>({ query, variables: varUpcoming });

              if (!upcomingData?.getEventsPaginated.result) {
                return;
              }

              const element = upcomingData.getEventsPaginated.result.find((e) => e.id === `${event.id}`);

              if (!element) {
                return;
              }

              const liveData = cache.readQuery<{ getEventsPaginated: { result: (string | { id: string; start_time: string; status: string })[] } }>({ query, variables: liveVariables });

              if (!liveData?.getEventsPaginated.result) {
                return;
              }

              const results = [
                ...liveData.getEventsPaginated.result,
                {
                  ...element,
                  start_time: DateTime.utc().toISO(),
                  status: `${Event.Phase.Live}`
                }
              ];

              cache.writeQuery({ query, data: { ...liveData, getEventsPaginated: {...liveData.getEventsPaginated, result: results }}, variables: liveVariables });
              cache.writeQuery({ query, data: { ...upcomingData, getEventsPaginated: {...upcomingData.getEventsPaginated, result: [] }}, variables: varUpcoming });

              setTimeout(() => {
                cache.modify({
                  id: `${Dbx.__typename}:${event.id}`,
                  fields: {
                    start_time(): string {
                      return DateTime.utc().toISO();
                    },
                    status(): string {
                      return `${Event.Phase.Live}`;
                    }
                  }
                });
              }, 100);
            }
          })
            .then(({ data }: FetchResult<TData>) => data?.manualEventStart)
            .catch((_error: ApolloError) => {
              throw Errors.serverError();
            });

          success_ = !!response;
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setSuccess(success_);

        return success_;
      },
      [mutation, setError]
    ),
    {
      success,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
